import { financialChange } from '../company-profile-template/financial-report';
import {
  KeyFigure, KeyFigureAdviser, KeyFigureChange, KeyFigureCheck, KeyFigureFlag, KeyFigureLimit, KeyFigurePoint, KeyFigureStrip,
  compactMoney, compactNumber, exactMoney, exactNumber, percent,
} from '../company-profile-template/key-figures';
import { TaxProfilePlay, taxProfilePeriodLabel } from './tax-profile-presenters';

/**
 * "Key figures for this relief" on the Tax profile. Each service tab reads ONE reference period: the latest filed
 * period that has all of the tab's core figures (otherwise the latest period, with the gaps shown as "Not filed").
 * Trends cover the newest five periods at most, matching the five accounts periods company_profile_data returns.
 * Each figure names the financial report tab that shows it.
 */

export const REPORT_TAB = {
  profitAndLoss: 'Profit and Loss Account',
  balanceSheet: 'Balance Sheet',
  assets: 'Asset Breakdown',
  other: 'Other Financial Metrics',
} as const;

const MAX_PERIODS = 5;

/**
 * Small company limits for financial years beginning on or after 6 April 2025: Companies Act 2006 s382(3), as amended
 * by The Companies (Accounts and Reports) (Amendment and Transitional Provision) Regulations 2024 (SI 2024/1303), which
 * raised turnover from £10.2m to £15m and the balance sheet total from £5.1m to £7.5m; the employee limit stays at 50.
 * A company is small when it meets at least two of the three in two consecutive years (s382(1) and (2)), and a small
 * company is entitled to audit exemption (s477), subject to the exclusions for public, regulated and some group
 * companies (s478, s479). The balance sheet total is gross assets, which the accounts extract files as Total Assets.
 */
export const SMALL_COMPANY_LIMITS = { turnover: 15_000_000, balanceSheetTotal: 7_500_000, employees: 50 } as const;

/** Period end (YYYY-MM-DD) to value. */
type Series = Map<string, number>;

interface AccountsIndex {
  /** Filed period ends, newest first. */
  periods: string[];
  rows: Map<string, Map<string, unknown>>;
  /** Periods with at least one profit and loss figure. */
  profitAndLossPeriods: Set<string>;
}

const MONTHS: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

function blank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function numeric(value: unknown): number | null {
  if (blank(value) || typeof value === 'boolean') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/** "Y.E. 31 Dec 2025" or "2025-12-31" to "2025-12-31"; null for anything else. */
export function taxPeriodIso(columnName: unknown): string | null {
  const name = String(columnName ?? '').replace(/^Y\.E\.\s*/i, '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(name)) return name;
  const match = /^(\d{1,2})\s+([A-Za-z]{3})[a-z]*\s+(\d{4})$/.exec(name);
  const month = match && MONTHS[match[2].toLowerCase()];
  return match && month ? `${match[3]}-${month}-${match[1].padStart(2, '0')}` : null;
}

function newestFirst(periods: Iterable<string>): string[] {
  return [...new Set(periods)].filter(Boolean).sort().reverse();
}

function accountsIndex(company: Record<string, any>): AccountsIndex {
  const accounts = company?.financials?.accounts || {};
  const madeUpTo: string[] = company?.financials?.made_up_to_dates || [];
  const rows = new Map<string, Map<string, unknown>>();
  const periods = new Set<string>();
  const profitAndLossPeriods = new Set<string>();
  for (const [sectionName, section] of Object.entries(accounts) as Array<[string, any]>) {
    const columns: any[] = Array.isArray(section?.columns) ? section.columns : [];
    const isos = columns.map((column, index) => taxPeriodIso(column?.column_name) || taxPeriodIso(madeUpTo[index]));
    for (const iso of isos) if (iso) periods.add(iso);
    for (const row of (section?.rows || []) as any[]) {
      const key = String(row?.name || '').toLowerCase();
      if (!key) continue;
      const values = rows.get(key) || new Map<string, unknown>();
      columns.forEach((column, index) => {
        const value = row?.data?.[column?.column_name];
        const iso = isos[index];
        if (!iso || blank(value)) return;
        values.set(iso, value);
        if (sectionName === REPORT_TAB.profitAndLoss && numeric(value) !== null) profitAndLossPeriods.add(iso);
      });
      rows.set(key, values);
    }
  }
  return { periods: newestFirst(periods), rows, profitAndLossPeriods };
}

function accountsSeries(index: AccountsIndex, name: string): Series {
  const series: Series = new Map();
  for (const [period, value] of index.rows.get(name.toLowerCase()) || []) {
    const number = numeric(value);
    if (number !== null) series.set(period, number);
  }
  return series;
}

function accountsText(index: AccountsIndex, name: string): Map<string, string> {
  const series = new Map<string, string>();
  for (const [period, value] of index.rows.get(name.toLowerCase()) || []) series.set(period, String(value).trim());
  return series;
}

/** The sum of the recorded tangible asset lines (e.g. additions) for each period with at least one of them. */
function assetSeries(company: Record<string, any>, keys: string[]): Series {
  const series: Series = new Map();
  for (const [period, classes] of Object.entries(company?.asset_breakdown || {}) as Array<[string, any]>) {
    const iso = taxPeriodIso(period);
    const values = keys.map(key => numeric(classes?.tangible?.[key])).filter((value): value is number => value !== null);
    if (iso && values.length) series.set(iso, values.reduce((sum, value) => sum + value, 0));
  }
  return series;
}

function energySeries(company: Record<string, any>, key: string): Series {
  const series: Series = new Map();
  for (const item of (company?.energy_and_emissions?.energy || []) as any[]) {
    const iso = taxPeriodIso(item?.made_up_to);
    const value = numeric(item?.[key]);
    if (iso && value !== null && !series.has(iso)) series.set(iso, value);
  }
  return series;
}

/** numerator / denominator * scale for each period with both and a non-zero denominator. */
function ratioSeries(numerator: Series, denominator: Series, scale: number): Series {
  const series: Series = new Map();
  for (const [period, value] of numerator) {
    const base = denominator.get(period);
    if (base === undefined || base === 0) continue;
    const ratio = value / base * scale;
    if (Number.isFinite(ratio)) series.set(period, ratio);
  }
  return series;
}

function sumSeries(a: Series, b: Series): Series {
  const series: Series = new Map();
  for (const [period, value] of a) if (b.has(period)) series.set(period, value + b.get(period)!);
  return series;
}

/** The newest five periods across the given period lists. */
function periodWindow(...lists: Iterable<string>[]): string[] {
  return newestFirst(lists.flatMap(list => [...list])).slice(0, MAX_PERIODS);
}

/**
 * The latest period with every core figure. Core series with nothing filed in any period are ignored, so a company
 * that never files one of them still gets its latest period; with no complete period, the latest period is used.
 */
export function taxReferencePeriod(periods: string[], core: Series[]): string {
  const filed = core.filter(series => series.size);
  return periods.find(period => filed.every(series => series.has(period))) || periods[0] || '';
}

function shortPeriodLabel(period: string): string {
  const date = new Date(`${period}T00:00:00Z`);
  if (!Number.isFinite(date.getTime())) return period;
  return new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}

export function taxYearEndedLabel(period: string): string {
  return period ? `Year ended ${taxProfilePeriodLabel(period)}` : 'No filed accounts';
}

type FigureFormat = 'money' | 'number' | 'percent' | 'kwh';

function formatValue(value: number, format: FigureFormat): { value: string; exact: string } {
  switch (format) {
    case 'money': return { value: compactMoney(value), exact: exactMoney(value) };
    case 'percent': return { value: percent(value), exact: percent(value, 2) };
    case 'kwh': return { value: compactNumber(value), exact: `${exactNumber(value)} kWh` };
    default: return { value: compactNumber(value), exact: exactNumber(value, 1) };
  }
}

/**
 * Year-on-year change against the prior period in the window. Amounts use the financial report's rule: a change that
 * is not finite or is over 1000% either way is not shown. Percentages move in percentage points.
 */
function figureChange(value: number | null, previous: number | null, format: FigureFormat): KeyFigureChange | null {
  if (value === null || previous === null) return null;
  if (format === 'percent') {
    const points = value - previous;
    if (!Number.isFinite(points)) return null;
    if (Math.abs(points) < 0.05) return { direction: 'flat', amount: '', text: 'Unchanged on the prior period' };
    const amount = `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Math.abs(points))} pts`;
    const direction = points > 0 ? 'up' : 'down';
    return { direction, amount, text: `${direction === 'up' ? 'Up' : 'Down'} ${amount} on the prior period` };
  }
  const { change, direction } = financialChange(value, previous);
  if (!direction) return null;
  if (direction === 'flat') return { direction, amount: '', text: 'Unchanged on the prior period' };
  const amount = change === null ? '' : `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: change < 10 ? 1 : 0 }).format(change)}%`;
  return { direction, amount, text: `${direction === 'up' ? 'Up' : 'Down'}${amount ? ' ' + amount : ''} on the prior period` };
}

interface FigureSpec {
  id: string;
  label: string;
  series: Series;
  format: FigureFormat;
  why: string;
  reportTab: string | null;
  unit?: string;
  detail?: (period: string) => string | undefined;
  trendKind?: 'line' | 'bars';
  /** When the reference period has no value, show the newest period that has one, labelled with its period. */
  latestIfMissing?: boolean;
  missingText?: string;
  /** Show the figure even when nothing is filed in any period. The first figure of a tab is always shown. */
  keep?: boolean;
  flag?: (value: number) => KeyFigureFlag | undefined;
  /** An action for the figure when it has a value, e.g. opening the energy history. */
  action?: { id: string; label: string };
}

function buildFigure(spec: FigureSpec, periods: string[], reference: string): KeyFigure {
  let period = reference;
  if (!spec.series.has(period) && spec.latestIfMissing) period = periods.find(candidate => spec.series.has(candidate)) || reference;
  const value = spec.series.get(period) ?? null;
  const index = periods.indexOf(period);
  const previousPeriod = index >= 0 ? periods[index + 1] : undefined;
  const previous = previousPeriod ? spec.series.get(previousPeriod) ?? null : null;
  const missingText = spec.missingText || 'Not filed';
  const shown = value === null ? { value: missingText, exact: missingText } : formatValue(value, spec.format);
  const trend: KeyFigurePoint[] = periods.slice().reverse().map(point => {
    const pointValue = spec.series.get(point) ?? null;
    return {
      period: point,
      label: shortPeriodLabel(point),
      value: pointValue,
      display: pointValue === null ? missingText : formatValue(pointValue, spec.format).value + (spec.format === 'kwh' ? ' kWh' : ''),
    };
  });
  return {
    id: spec.id,
    label: spec.label,
    value: shown.value,
    exact: shown.exact,
    missing: value === null,
    unit: value === null ? undefined : spec.unit,
    detail: value === null ? undefined : spec.detail?.(period),
    why: spec.why,
    reportTab: spec.reportTab,
    periodLabel: period && period !== reference ? taxYearEndedLabel(period) : undefined,
    change: figureChange(value, previous, spec.format),
    trend,
    trendKind: spec.trendKind || 'line',
    flag: value === null ? undefined : spec.flag?.(value),
    ...(value === null || !spec.action ? {} : { action: spec.action }),
  };
}

/** Builds the figures, leaving out those with nothing filed in any period (other than the tab's first and kept ones). */
function buildFigures(specs: FigureSpec[], periods: string[], reference: string): KeyFigure[] {
  return specs.filter((spec, index) => index === 0 || spec.keep || spec.series.size)
    .map(spec => buildFigure(spec, periods, reference));
}

function profitAndLossNotice(index: AccountsIndex, reference: string): string | undefined {
  return reference && index.periods.includes(reference) && !index.profitAndLossPeriods.has(reference)
    ? 'No profit and loss account was filed for this period, which is usual for small and abridged accounts.'
    : undefined;
}

function strip(index: AccountsIndex, reference: string, figures: KeyFigure[], extra: Partial<KeyFigureStrip> = {}): KeyFigureStrip {
  const accountType = accountsText(index, 'Account Type').get(reference);
  return {
    period: reference,
    periodLabel: taxYearEndedLabel(reference),
    accountType: accountType || undefined,
    notice: profitAndLossNotice(index, reference),
    figures,
    reportTab: figures.find(figure => figure.reportTab)?.reportTab || REPORT_TAB.profitAndLoss,
    ...extra,
  };
}

function rdFigures(company: Record<string, any>): KeyFigureStrip {
  const index = accountsIndex(company);
  const wages = accountsSeries(index, 'Wages and Salaries');
  const revenue = accountsSeries(index, 'Revenue');
  const employees = accountsSeries(index, 'Number of Employees');
  const taxCredit = accountsSeries(index, 'Tax Credit');
  const periods = periodWindow(index.periods);
  const reference = taxReferencePeriod(periods, [wages, revenue]);
  const specs: FigureSpec[] = [
    { id: 'wages', label: 'Wages and salaries', series: wages, format: 'money', reportTab: REPORT_TAB.other,
      why: 'Staff costs are usually the largest part of an R&D claim.' },
    { id: 'wages-to-revenue', label: 'Wages to revenue', series: ratioSeries(wages, revenue, 100), format: 'percent',
      unit: 'of revenue', reportTab: REPORT_TAB.other, why: 'A people-heavy cost base suggests technical work done in house.' },
    { id: 'pay-per-employee', label: 'Average pay per employee', series: ratioSeries(wages, employees, 1), format: 'money',
      reportTab: REPORT_TAB.other, why: 'Higher average pay often points to skilled technical staff.',
      detail: period => employees.has(period) ? `${exactNumber(employees.get(period)!)} employees` : undefined },
  ];
  if (!wages.size && employees.size) {
    specs.push({ id: 'employees', label: 'Employees', series: employees, format: 'number', reportTab: REPORT_TAB.other,
      why: 'Headcount gives the scale of any R&D team when staff costs are not filed.' });
  }
  if (taxCredit.size) {
    specs.push({ id: 'tax-credit', label: 'Tax credit in the accounts', series: taxCredit, format: 'money', trendKind: 'bars',
      reportTab: REPORT_TAB.profitAndLoss, why: 'A tax credit line can reflect an R&D claim for that year.' });
  }
  return strip(index, reference, buildFigures(specs, periods, reference));
}

function capitalAllowancesFigures(company: Record<string, any>): KeyFigureStrip {
  const index = accountsIndex(company);
  const plantAdditions = assetSeries(company, ['plant_and_machinery_additions']);
  const plantHeld = assetSeries(company, ['plant_and_machinery']);
  // Some accounts give only the closing net book value, not the year's additions; that still shows the kit held.
  const plant = plantAdditions.size ? plantAdditions : plantHeld;
  const buildings = assetSeries(company, ['land_and_buildings_additions']);
  const equipment = assetSeries(company, ['furniture_and_fittings_additions', 'vehicles_additions', 'computer_equipment_additions']);
  const tax = accountsSeries(index, 'Corporation Tax');
  const periods = periodWindow(index.periods, plant.keys(), buildings.keys(), equipment.keys());
  const reference = taxReferencePeriod(periods, [plant, tax]);
  const specs: FigureSpec[] = [
    plantAdditions.size || !plantHeld.size
      ? { id: 'plant-additions', label: 'Plant and machinery additions', series: plant, format: 'money', trendKind: 'bars',
        reportTab: REPORT_TAB.assets, why: 'Spend that may qualify for full expensing or the annual investment allowance.' }
      : { id: 'plant-held', label: 'Plant and machinery held', series: plant, format: 'money', unit: 'net book value',
        reportTab: REPORT_TAB.assets, why: 'Additions are not broken out; the kit held shows the scale of qualifying assets.' },
  ];
  if (buildings.size) {
    specs.push({ id: 'buildings-additions', label: 'Land and buildings additions', series: buildings, format: 'money', trendKind: 'bars',
      reportTab: REPORT_TAB.assets, why: 'Building work can carry structures and buildings allowance and embedded fixtures.' });
  }
  if (equipment.size) {
    specs.push({ id: 'equipment-additions', label: 'Other equipment additions', series: equipment, format: 'money', trendKind: 'bars',
      reportTab: REPORT_TAB.assets, detail: () => 'Fittings, vehicles and computers',
      why: 'Vehicles, computers and fittings can also attract allowances.' });
  }
  specs.push({ id: 'corporation-tax', label: 'Corporation tax charge', series: tax, format: 'money',
    reportTab: REPORT_TAB.profitAndLoss, why: 'Allowances save cash only when there is taxable profit to set them against.',
    flag: value => value <= 0 ? { tone: 'caution', text: 'No tax charge this period, so new allowances may not save tax yet' } : undefined });
  return strip(index, reference, buildFigures(specs, periods, reference));
}

function sizeOutcome(limits: KeyFigureLimit[]): boolean | null {
  const within = limits.filter(limit => limit.within === true).length;
  const over = limits.filter(limit => limit.within === false).length;
  return within >= 2 ? true : over >= 2 ? false : null;
}

function sizeLimits(revenue: Series, assets: Series, employees: Series, period: string): KeyFigureLimit[] {
  const limit = (label: string, series: Series, max: number, format: 'money' | 'number', reportTab: string): KeyFigureLimit => {
    const value = series.get(period) ?? null;
    return {
      label,
      value: value === null ? 'Not filed' : format === 'money' ? compactMoney(value) : exactNumber(value),
      limit: format === 'money' ? compactMoney(max).replace('.0m', 'm') : exactNumber(max),
      ratio: value === null ? null : Math.max(0, value) / max,
      within: value === null ? null : value <= max,
      reportTab,
    };
  };
  return [
    limit('Turnover', revenue, SMALL_COMPANY_LIMITS.turnover, 'money', REPORT_TAB.profitAndLoss),
    limit('Balance sheet total', assets, SMALL_COMPANY_LIMITS.balanceSheetTotal, 'money', REPORT_TAB.balanceSheet),
    limit('Employees', employees, SMALL_COMPANY_LIMITS.employees, 'number', REPORT_TAB.other),
  ];
}

/** The small-company (audit exemption) size test for the reference period, with the prior period for context. */
export function taxSmallCompanyCheck(company: Record<string, any>, reference?: string): KeyFigureCheck | undefined {
  const index = accountsIndex(company);
  const revenue = accountsSeries(index, 'Revenue');
  const assets = accountsSeries(index, 'Total Assets');
  const employees = accountsSeries(index, 'Number of Employees');
  const periods = periodWindow(index.periods);
  const period = reference ?? taxReferencePeriod(periods, [revenue, assets, employees]);
  if (!period) return undefined;
  const limits = sizeLimits(revenue, assets, employees, period);
  const outcome = sizeOutcome(limits);
  const met = limits.filter(limit => limit.within === true).length;
  const filed = limits.filter(limit => limit.within !== null).length;
  const priorPeriod = periods[periods.indexOf(period) + 1];
  const priorOutcome = priorPeriod ? sizeOutcome(sizeLimits(revenue, assets, employees, priorPeriod)) : null;
  const prior = priorOutcome === null ? undefined
    : `Prior period: ${priorOutcome === outcome ? 'also ' : ''}${priorOutcome ? 'within' : 'above'} the limits`;
  const auditor = String(company?.financials?.financial_providers?.auditor || '').trim();
  return {
    eyebrow: 'Audit exemption test',
    result: outcome === null ? 'Too few filed figures to test' : outcome ? 'Within the small-company limits' : 'Above the small-company limits',
    summary: outcome === null && filed < 3
      ? `${filed} of 3 limits filed for the year ended ${taxProfilePeriodLabel(period)}`
      : `Meets ${met} of 3 for the year ended ${taxProfilePeriodLabel(period)}`,
    outcome,
    prior,
    limits,
    basis: 'Limits for years from 6 April 2025. A company is small when it meets 2 of the 3 in two years running.',
    flag: outcome && priorOutcome && auditor
      ? { tone: 'notable', text: 'Audited while within the limits, so the audit may be voluntary' } : undefined,
  };
}

/**
 * Accountant, auditor and banker. The names come from the latest accounts (financials.financial_providers, from the
 * suppliers extracted from the latest year's accounts). With no banker named, the holder of the newest outstanding
 * charge stands in, as the lender most likely to bank the company.
 */
export function taxAdvisers(company: Record<string, any>): KeyFigureAdviser[] {
  const providers = company?.financials?.financial_providers || {};
  const latest = taxPeriodIso(company?.financials?.made_up_to_dates?.[0]);
  const accountsPeriod = latest ? `Accounts to ${taxProfilePeriodLabel(latest)}` : undefined;
  const named = (role: string, value: unknown): KeyFigureAdviser => {
    const name = String(value || '').trim();
    return name
      ? { role, name, source: 'Named in the accounts', period: accountsPeriod }
      : { role, name: null, source: 'Checked the latest accounts' };
  };
  let banker = named('Banker', providers.banker);
  if (!banker.name) {
    const charge = ((company?.company_charges || []) as any[])
      .filter(item => /outstanding|part/i.test(String(item?.status || '')) && String(item?.persons_entitled?.[0]?.name || '').trim())
      .sort((a, b) => String(b?.created_on || '').localeCompare(String(a?.created_on || '')))[0];
    const created = taxPeriodIso(charge?.created_on);
    if (charge) {
      banker = {
        role: 'Banker',
        name: String(charge.persons_entitled[0].name).trim(),
        source: 'Holds an outstanding charge',
        period: created ? `Registered ${taxProfilePeriodLabel(created)}` : undefined,
      };
    }
  }
  return [named('Accountant', providers.accountant), named('Auditor', providers.auditor), banker];
}

function accountsAuditFigures(company: Record<string, any>): KeyFigureStrip {
  const index = accountsIndex(company);
  const revenue = accountsSeries(index, 'Revenue');
  const assets = accountsSeries(index, 'Total Assets');
  const employees = accountsSeries(index, 'Number of Employees');
  const profit = accountsSeries(index, 'Profit Before Tax');
  const types = accountsText(index, 'Account Type');
  const periods = periodWindow(index.periods);
  const reference = taxReferencePeriod(periods, [revenue, assets, employees]);
  const type = types.get(reference);
  // The type before the reference period's, when it changed, e.g. a move from small to medium accounts.
  const earlier = periods.slice(periods.indexOf(reference) + 1).find(period => types.has(period) && types.get(period) !== type);
  const accountsFiled: KeyFigure = {
    id: 'accounts-filed',
    label: 'Accounts filed',
    value: type || 'Not recorded',
    exact: type || 'Not recorded',
    missing: !type,
    detail: type && earlier ? `${types.get(earlier)} for ${shortPeriodLabel(earlier)}`
      : type && periods.filter(period => types.get(period) === type).length > 1 ? `Same type since ${shortPeriodLabel(periods.filter(period => types.get(period) === type).pop()!)}` : undefined,
    why: 'The accounts type sets how much the company must disclose each year.',
    reportTab: REPORT_TAB.other,
    change: null,
    trend: [],
    trendKind: 'line',
  };
  const figures = [
    accountsFiled,
    buildFigure({ id: 'profit-before-tax', label: 'Profit before tax', series: profit, format: 'money',
      reportTab: REPORT_TAB.profitAndLoss, why: 'Profit drives the tax computation and the planning conversation.' }, periods, reference),
  ];
  return strip(index, reference, figures, {
    // The accounts filed figure already names the type, so the heading does not repeat it.
    accountType: undefined,
    check: taxSmallCompanyCheck(company, reference || undefined),
    advisers: taxAdvisers(company),
  });
}

function energyFigures(company: Record<string, any>): KeyFigureStrip {
  const index = accountsIndex(company);
  const ebitda = accountsSeries(index, 'EBITDA');
  const wages = accountsSeries(index, 'Wages and Salaries');
  const gva = sumSeries(ebitda, wages);
  const total = energySeries(company, 'total_energy_kwh');
  const electricity = energySeries(company, 'electric_kwh');
  const gas = energySeries(company, 'gas_kwh');
  const periods = periodWindow(index.periods, total.keys());
  const reference = taxReferencePeriod(periods, [ebitda, wages, total]);
  const specs: FigureSpec[] = [
    { id: 'gva', label: 'GVA proxy', series: gva, format: 'money', reportTab: REPORT_TAB.profitAndLoss,
      detail: () => 'EBITDA plus wages and salaries', why: 'Energy-intensive relief tests compare energy costs with gross value added.' },
    { id: 'energy', label: 'Energy use', series: total, format: 'kwh', unit: 'kWh', trendKind: 'bars', reportTab: null,
      latestIfMissing: true, keep: true, action: { id: 'energy-history', label: 'View energy history' },
      missingText: 'Not recorded', why: 'Recorded consumption from the energy and carbon report.',
      detail: period => [
        electricity.has(period) ? `Electricity ${compactNumber(electricity.get(period)!)}` : '',
        gas.has(period) ? `Gas ${compactNumber(gas.get(period)!)}` : '',
      ].filter(Boolean).join(' · ') || undefined },
  ];
  if (total.size && gva.size) {
    specs.push({ id: 'energy-intensity', label: 'Energy per £1k of GVA', series: ratioSeries(total, gva, 1000), format: 'number',
      unit: 'kWh', reportTab: null, missingText: 'Not recorded',
      why: 'Energy use against value added; higher means a more energy-intensive operation.' });
  }
  return strip(index, reference, buildFigures(specs, periods, reference));
}

/** Gross margins below this percentage are flagged on International tax. */
export const THIN_GROSS_MARGIN_PERCENT = 5;

function internationalFigures(company: Record<string, any>): KeyFigureStrip {
  const index = accountsIndex(company);
  const revenue = accountsSeries(index, 'Revenue');
  const grossProfit = accountsSeries(index, 'Gross Profit');
  const fx = accountsSeries(index, 'FX Gain or Loss');
  const periods = periodWindow(index.periods);
  const reference = taxReferencePeriod(periods, [revenue, grossProfit]);
  const specs: FigureSpec[] = [
    { id: 'gross-margin', label: 'Gross margin', series: ratioSeries(grossProfit, revenue, 100), format: 'percent',
      reportTab: REPORT_TAB.profitAndLoss, why: 'Thin or shifting margins can prompt transfer-pricing questions for group traders.',
      detail: period => grossProfit.has(period) ? `Gross profit ${compactMoney(grossProfit.get(period)!)}` : undefined,
      flag: value => value < THIN_GROSS_MARGIN_PERCENT
        ? { tone: 'caution', text: `Below ${THIN_GROSS_MARGIN_PERCENT}%: worth a transfer-pricing conversation` } : undefined },
    { id: 'revenue', label: 'Revenue', series: revenue, format: 'money', reportTab: REPORT_TAB.profitAndLoss,
      why: 'The scale of trade that any cross-border pricing applies to.' },
  ];
  if (fx.size) {
    specs.push({ id: 'fx', label: 'FX gain or loss', series: fx, format: 'money', trendKind: 'bars', reportTab: REPORT_TAB.other,
      why: 'Currency gains or losses show trade in foreign currencies.' });
  }
  return strip(index, reference, buildFigures(specs, periods, reference));
}

/** The key figures strip for a Tax service tab. */
export function taxKeyFigures(company: Record<string, any>, play: TaxProfilePlay): KeyFigureStrip {
  switch (play) {
    case 'rd': return rdFigures(company);
    case 'capital_allowances': return capitalAllowancesFigures(company);
    case 'accounts_audit': return accountsAuditFigures(company);
    case 'energy': return energyFigures(company);
    default: return internationalFigures(company);
  }
}

/** Whether a strip has anything to show: a filed figure, a size test or a named adviser. */
export function hasTaxKeyFigures(keyFigures: KeyFigureStrip | null | undefined): boolean {
  return !!keyFigures && (keyFigures.figures.some(figure => !figure.missing) || !!keyFigures.check ||
    !!keyFigures.advisers?.some(adviser => adviser.name));
}
